import React, { useEffect, useState } from 'react';
import {
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
} from '@chakra-ui/react';
import { AttributeInput } from 'src/components/atoms';
import { Attribute, AttributeOption, FlatAttributeValuesType, ValueObject } from 'src/api/v1-api';
import { BasicInfoType, FilePath } from 'src/components/organisms/listing-form';
import useRequest from 'src/utils/hooks/useRequest';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { useContext } from 'react';

interface AttributeValueCaptureModalProps {
  attributeIds: number[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (e?: any) => void;
  modalHeader?: string;
  alertText?: string;
  attributeValues?: FlatAttributeValuesType;
  resetAttributeValues?: (values: FlatAttributeValuesType) => void;
  onAttributeChange?: (
    attributeId: number,
    value: string | number | boolean | ValueObject | FilePath[] | AttributeOption[],
  ) => void;
  requireAllAttributes?: boolean;
  createAttributeValues?: boolean;
}

const AttributeValueCaptureModal: React.FC<AttributeValueCaptureModalProps> = ({
  attributeIds,
  isOpen,
  onClose,
  onSave,
  modalHeader,
  alertText,
  attributeValues,
  onAttributeChange,
  resetAttributeValues,
  requireAllAttributes = false,
  createAttributeValues = false,
}) => {
  const { api } = useRequest();
  const { marketplace } = useContext(MarketplaceContext);
  const showToast = useSingleToast();
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [values, setValues] = useState<FlatAttributeValuesType>({});
  const [isSaving, setIsSaving] = useState(false);
  const [originalValues, setOriginalValues] = useState<FlatAttributeValuesType>({});

  useEffect(() => {
    setValues(attributeValues ?? {});
  }, [attributeValues]);

  useEffect(() => {
    if (isOpen) {
      setValues(attributeValues ?? {});
      setOriginalValues(attributeValues ?? {});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!attributeIds.length || attributes.length) return;

    const fetchAttributes = async () => {
      try {
        const responses = await Promise.all(attributeIds.map((id) => api.attributes.get(id)));
        setAttributes(responses);
      } catch (error) {
        showToast({
          title: 'Something went wrong',
          description: 'Please try again. If the problem persists, contact support.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'bottom',
        });
      }
    };

    fetchAttributes();
  }, [attributeIds, api, showToast]);

  const beforeClose = () => {
    if (!createAttributeValues) {
      resetAttributeValues?.(originalValues);
    }

    onClose();
  };

  const handleSave = async (e: any) => {
    e?.preventDefault();
    try {
      setIsSaving(true);
      if (createAttributeValues) {
        await Promise.all(
          attributes.map((attribute) =>
            api.attributes.createValue(marketplace?.id as number, attribute.id, {
              value: values[attribute.id],
            }),
          ),
        );
        showToast({
          title: "We've updated your info",
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'bottom',
        });
      }
      onSave(e);
    } catch (error) {
      showToast({
        title: 'Something went wrong',
        description: 'Please try again. If the problem persists, contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAttributeChange = (
    attributeId: number,
    value: string | number | boolean | ValueObject | FilePath[] | AttributeOption[],
  ) => {
    if (onAttributeChange) {
      onAttributeChange(attributeId, value);
    }
    setValues((prev) => ({
      ...prev,
      [attributeId]: value,
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={beforeClose}>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSave}>
        <ModalHeader>{modalHeader}</ModalHeader>
        <ModalCloseButton color="black" />
        <ModalBody>
          {alertText && (
            <Alert status="warning" my={6}>
              <AlertIcon />
              {alertText}
            </Alert>
          )}
          <Flex direction="column" gap={4} mb={4}>
            {attributes.length ? (
              attributes
                .sort((a, b) => a.position - b.position)
                .map((attribute) => (
                  <AttributeInput<BasicInfoType>
                    key={attribute.id}
                    attribute={attribute}
                    width="full"
                    isMobileView
                    value={values[attribute.id] || ''}
                    setChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
                    setSelectChange={(e) => handleAttributeChange(attribute.id, e.target.value)}
                    setSwitchChange={(checked) => handleAttributeChange(attribute.id, checked)}
                    disabled={false}
                    setMediaChange={(filePaths) => handleAttributeChange(attribute.id, filePaths)}
                    setMultiselectChange={(value) => handleAttributeChange(attribute.id, value)}
                    requiredOverride={requireAllAttributes}
                    textColor="black"
                  />
                ))
            ) : (
              <Center width="100%" display="flex" justifyContent="center" h="100px">
                <Spinner />
              </Center>
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button type="submit" colorScheme="primary" mr={3} isLoading={isSaving}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AttributeValueCaptureModal;
